import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactPlayer from 'react-player';
import DOMPurify from 'dompurify';
import cns from 'classnames';
import { connect } from 'react-redux';
import { isIE } from '../../shared/utils/WindowUtils';
import normalizeVideoUrl from '../../shared/utils/VideoUtils';
import { injectMediaAuthToken } from '../../shared/utils/MediaAuthUtils';

class VideoPlayer extends Component {
    static propTypes = {
        src: PropTypes.string.isRequired,
        className: PropTypes.string,
        allowControls: PropTypes.bool,
        playByDefault: PropTypes.bool,
        idToken: PropTypes.string,
    };

    constructor(props) {
        super(props);
        this.containerRef = React.createRef();
    }

    componentDidMount() {
        // Inject authentication tokens into media URLs after initial render
        if (this.containerRef.current && this.props.idToken) {
            injectMediaAuthToken(this.containerRef.current, this.props.idToken).catch((error) => {
                console.warn('Error injecting media auth tokens:', error);
            });
        }
    }

    componentDidUpdate() {
        // Re-inject authentication tokens when src changes
        if (this.containerRef.current && this.props.idToken) {
            injectMediaAuthToken(this.containerRef.current, this.props.idToken).catch((error) => {
                console.warn('Error injecting media auth tokens:', error);
            });
        }
    }

    _renderPlayer = (src) => {
        const { allowControls = true, playByDefault = false } = this.props;

        if (ReactPlayer.canPlay(src)) {
            return (
                <ReactPlayer
                    url={src}
                    playing={playByDefault}
                    controls={allowControls}
                    width={'100%'}
                    height={'100%'}
                    className={'player'}
                />
            );
        }

        return (
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(src, { ALLOWED_TAGS: ['div', 'iframe'] }) }} />
        );
    };

    /**
     * @param {String} src
     *
     * this method handles rendering the player for IE
     * it is a hack and I'm truly sorry
     *
     * @returns {JSX}
     */
    _renderPlayerForIE = (src) => {
        if (src.indexOf('iframe') > -1) {
            return (
                <div
                    style={{ display: 'block', margin: 'auto' }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(src) }}
                />
            );
        }

        if (src.indexOf('youtube') > -1 || src.indexOf('youtu.be') > -1 || src.indexOf('vimeo') > -1) {
            return <iframe className={'player'} src={normalizeVideoUrl(src)} frameborder={'0'} />;
        }

        return (
            <video className={'player'} controls height={'100%'} width={'100%'}>
                <source src={src} type={'video/mp4'} />
                <source src={src} type={'video/webm'} />
                <p>
                    Your browser doesn't support HTML5 video. Here is a <a href={src}>link to the video</a> instead.
                </p>
            </video>
        );
    };

    render() {
        const { src, className = '' } = this.props;
        return (
            <div
                ref={this.containerRef}
                className={cns('VideoPlayer', className, {
                    'react-player': !isIE() && src.indexOf('iframe') === -1,
                    'ie-player': isIE()
                })}
            >
                {!isIE() && ReactPlayer !== undefined ? this._renderPlayer(src) : this._renderPlayerForIE(src)}
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    const {
        auth: { idToken }
    } = state;

    return {
        idToken
    };
};

export const VideoPlayerComponent = VideoPlayer;
export default connect(mapStateToProps)(VideoPlayer);
